import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'atoms/Toast'
import {
  generateArtifact,
  getJobRequest,
  JobRequestResponse,
} from 'services/kraken-core/translation_maps/translationMaps.service'
import {
  getStoredJobRequest,
  isJobRequestExpired,
  JOB_REQUEST_MAX_AGE_MS,
  removeStoredJobRequest,
  setStoredJobRequest,
  StoredJobRequest,
} from '../jobRequestStorage'
import { QUERIES } from '../types'

const GENERATE_ARTIFACT_JOB_TYPE = 'generate_artifact'

type Variant = 'success' | 'error' | 'warning' | 'info'

function JobToastContent({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: '0.875em', opacity: 0.85 }}>{description}</div>
    </div>
  )
}

function showJobToast(variant: Variant, title: string, description: string) {
  const content = <JobToastContent title={title} description={description} />
  switch (variant) {
    case 'success':
      toast.success(content)
      return
    case 'error':
      toast.error(content)
      return
    case 'warning':
      toast.warning(content)
      return
    default:
      toast.info(content)
  }
}

function showCompletedToast() {
  showJobToast('success', 'Code generated', 'Your translation map artifact is ready.')
}

function showFailedToast(description?: string) {
  showJobToast(
    'error',
    'Code generation failed',
    description || 'The generation job finished with an error. Please try again.'
  )
}

function showTimeoutToast() {
  showJobToast(
    'warning',
    'Code generation timed out',
    'The job took longer than 25 minutes. We stopped tracking it. Please try again.'
  )
}

export interface UseArtifactGenerationOptions {
  onCompleted?: () => void
}

export interface UseArtifactGenerationResult {
  isRunning: boolean
  generate: () => void
}

export function useArtifactGeneration(
  translationMapId?: string,
  { onCompleted }: UseArtifactGenerationOptions = {}
): UseArtifactGenerationResult {
  const queryClient = useQueryClient()
  const [isRunning, setIsRunning] = useState(false)

  const onCompletedRef = useRef(onCompleted)
  useEffect(() => {
    onCompletedRef.current = onCompleted
  }, [onCompleted])

  const runningJobRef = useRef<StoredJobRequest | null>(null)
  const cleanupRef = useRef<(() => void) | null>(null)
  const recoveredTranslationMapIdRef = useRef<string | null>(null)
  const hasUnmountedRef = useRef(false)

  const invalidateMapQueries = useCallback(
    (id: string) => {
      queryClient.invalidateQueries([QUERIES.GET_TRANSLATION_MAP, id])
      queryClient.invalidateQueries([QUERIES.SEARCH_TRANSLATION_MAP])
    },
    [queryClient]
  )

  const finishJob = useCallback(() => {
    const active = runningJobRef.current
    if (active) {
      removeStoredJobRequest(active.related_object_uuid)
      runningJobRef.current = null
    }
    if (cleanupRef.current) {
      cleanupRef.current()
      cleanupRef.current = null
    }
    if (!hasUnmountedRef.current) {
      setIsRunning(false)
    }
  }, [])

  const awaitJob = useCallback(
    (job: StoredJobRequest, remainingMs: number) => {
      // Dynamic require mirrors the existing pattern used by useGenerateArtifact.
      const { getPusherClient } = require('common/pusher/pusherClient')
      const pusher = getPusherClient()
      const channel = pusher.subscribe(job.channel)

      const timeout = setTimeout(() => {
        finishJob()
        showTimeoutToast()
      }, Math.max(remainingMs, 0))

      const handleCompleted = () => {
        invalidateMapQueries(job.related_object_uuid)
        finishJob()
        showCompletedToast()
        onCompletedRef.current?.()
      }

      const onFailed = () => {
        finishJob()
        showFailedToast()
      }

      channel.bind('job.completed', handleCompleted)
      channel.bind('job.failed', onFailed)

      cleanupRef.current = () => {
        clearTimeout(timeout)
        channel.unbind?.('job.completed', handleCompleted)
        channel.unbind?.('job.failed', onFailed)
        pusher.unsubscribe(job.channel)
      }
    },
    [finishJob, invalidateMapQueries]
  )

  const generate = useCallback(async () => {
    if (!translationMapId || isRunning) return

    setIsRunning(true)
    try {
      const { response, error } = await generateArtifact(translationMapId)
      if (error || !response?.data) throw error || new Error('Missing generateArtifact response')

      const { job_uuid, job_request_id, channel } = response.data
      const stored: StoredJobRequest = {
        id: job_request_id,
        type: GENERATE_ARTIFACT_JOB_TYPE,
        related_object_uuid: translationMapId,
        job: { uuid: job_uuid, status: 'running' },
        channel,
        created_at: new Date().toISOString(),
      }
      setStoredJobRequest(stored)
      if (hasUnmountedRef.current) return
      runningJobRef.current = stored
      awaitJob(stored, JOB_REQUEST_MAX_AGE_MS)
    } catch (err) {
      if (!hasUnmountedRef.current) setIsRunning(false)
      runningJobRef.current = null
      const message = (err as any)?.response?.data?.message || (err as any)?.message || undefined
      showFailedToast(message)
    }
  }, [translationMapId, awaitJob, isRunning])

  useEffect(() => {
    return () => {
      hasUnmountedRef.current = true
      if (cleanupRef.current) {
        cleanupRef.current()
        cleanupRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!translationMapId) return
    if (recoveredTranslationMapIdRef.current === translationMapId) return
    recoveredTranslationMapIdRef.current = translationMapId

    const stored = getStoredJobRequest(translationMapId)
    if (!stored) return

    let cancelled = false

    const recoverStoredJob = async () => {
      if (isJobRequestExpired(stored)) {
        removeStoredJobRequest(translationMapId)
        showTimeoutToast()
        return
      }

      const { response, error } = await getJobRequest(stored.id)
      if (cancelled) return

      if (error || !response?.data) {
        removeStoredJobRequest(translationMapId)
        return
      }

      const data = response.data as JobRequestResponse
      const status = data.job?.status

      if (status === 'finished') {
        removeStoredJobRequest(translationMapId)
        invalidateMapQueries(translationMapId)
        showCompletedToast()
        onCompletedRef.current?.()
        return
      }

      if (status === 'error') {
        removeStoredJobRequest(translationMapId)
        showFailedToast()
        return
      }

      if (status === 'timeout') {
        removeStoredJobRequest(translationMapId)
        showTimeoutToast()
        return
      }

      if (status !== 'running') {
        removeStoredJobRequest(translationMapId)
        return
      }

      if (isJobRequestExpired(stored)) {
        removeStoredJobRequest(translationMapId)
        showTimeoutToast()
        return
      }

      const createdAtMs = new Date(stored.created_at).getTime()
      const remainingMs = createdAtMs + JOB_REQUEST_MAX_AGE_MS - Date.now()

      runningJobRef.current = stored
      setIsRunning(true)
      awaitJob(stored, remainingMs)
    }

    recoverStoredJob()

    return () => {
      cancelled = true
    }
  }, [translationMapId, awaitJob, invalidateMapQueries])

  return { isRunning, generate }
}
